import {
  CancelIconButton,
  Card,
  Checkbox,
  DataDisplay,
  DeleteIconButton,
  UpdateIconButton,
  Grid,
  Stack,
  ValidationErrors,
  pn,
  useElementBreakpoints,
  Tooltip,
  CANNOT_DELETE_ITEM_IN_USE_MSG,
  formatBoolean,
} from "@compass/core";
import { InlineEditDataModel, Input, Select } from "components";
import {
  Permission,
  PermissionAction,
  StudentServiceSubType,
  StudentServiceSubTypeEditResource,
  StudentServiceSubTypeViewResource,
} from "generated/openApi";
import { useInlineEditDataModel } from "hooks";
import produce from "immer";
import "moment-timezone";
import { memo } from "react";
import { isAuthorized } from "services";

export const ServiceSubType = memo(
  (props: {
    rootPermission: Permission;
    serviceSubType: StudentServiceSubType;
    setServiceSubType: (value: StudentServiceSubType) => void;
    viewResource: StudentServiceSubTypeViewResource;
    editResource: StudentServiceSubTypeEditResource;
    validationErrors: ValidationErrors | string[];
    cancelCreate: () => void;
  }) => {
    // console.log("rerender");

    const { breakpointsRef, smallFieldBreakpoints, xSmallFieldBreakpoints } = useElementBreakpoints();
    const properties = props.viewResource.properties || [];

    const { canCancel, canEdit, canRemove, cancel, edit, remove, editing, removing } = useInlineEditDataModel({
      dataModel: props.serviceSubType,
      setDataModel: props.setServiceSubType,
      cancelCreate: props.cancelCreate,
    });

    const nameField = () => {
      const propertyName = pn<StudentServiceSubType>("name");
      const property = properties.find((x) => x.propertyName === propertyName);

      return !editing ? (
        <DataDisplay label={property?.displayName}>{props.serviceSubType.name}</DataDisplay>
      ) : (
        <Input
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.serviceSubType.name}
          onChange={(e) =>
            props.setServiceSubType(
              produce(props.serviceSubType, (draft) => {
                draft.name = e.target.value;
              })
            )
          }
        />
      );
    };

    const inactiveField = () => {
      const propertyName = pn<StudentServiceSubType>("inactive");
      const property = properties.find((x) => x.propertyName === propertyName);

      return !editing ? (
        <DataDisplay label={property?.displayName}>{formatBoolean(props.serviceSubType.inactive)}</DataDisplay>
      ) : (
        <Stack height="100%" justifyContent="center">
          <Checkbox
            label={property?.displayName}
            checked={props.serviceSubType.inactive}
            onChange={(e) =>
              props.setServiceSubType(
                produce(props.serviceSubType, (draft) => {
                  draft.inactive = e.target.checked;
                })
              )
            }
          />
        </Stack>
      );
    };

    const ccrecStudentServiceSubTypeField = () => {
      const propertyName = pn<StudentServiceSubType>("ccrecStudentServiceSubTypeId");
      const property = properties.find((x) => x.propertyName === propertyName);

      return !editing ? (
        <DataDisplay label={property?.displayName}>{props.serviceSubType.ccrecStudentServiceSubType?.name}</DataDisplay>
      ) : (
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.editResource.ccrecServiceSubTypes?.find(
            (x) => x.id === props.serviceSubType.ccrecStudentServiceSubTypeId
          )}
          options={props.editResource.ccrecServiceSubTypes}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setServiceSubType(
              produce(props.serviceSubType, (draft) => {
                draft.ccrecStudentServiceSubTypeId = value?.id;
              })
            )
          }
        />
      );
    };

    return (
      <Card>
        <InlineEditDataModel
          noPadding
          noBorder
          removing={removing}
          actions={
            <>
              {isAuthorized(props.rootPermission, PermissionAction.Update) && canEdit && (
                <UpdateIconButton onClick={edit} size="small" />
              )}
              {isAuthorized(props.rootPermission, PermissionAction.Delete) && canRemove && (
                <Tooltip title={props.serviceSubType.isInUse ? CANNOT_DELETE_ITEM_IN_USE_MSG : ""}>
                  <DeleteIconButton onClick={remove} size="small" disabled={props.serviceSubType.isInUse} />
                </Tooltip>
              )}
              {canCancel && <CancelIconButton onClick={cancel} size="small" />}
            </>
          }
          content={
            <Grid ref={breakpointsRef} container spacing="sm">
              <Grid item xs={smallFieldBreakpoints}>
                {nameField()}
              </Grid>
              <Grid item xs={smallFieldBreakpoints}>
                {ccrecStudentServiceSubTypeField()}
              </Grid>
              <Grid item xs={xSmallFieldBreakpoints}>
                {inactiveField()}
              </Grid>
            </Grid>
          }
        />
      </Card>
    );
  }
);
